import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { storeDispatch } from '../state/Store';
import * as Actions from '../state/actions';
import { MAX_MUSIC_DURATION_WEB_SECONDS } from '../data/Config';
import WaveformView from '../audio/WaveformView';
import { Soundtrack } from '../audio/Soundtrack';
import { getAudioContext } from '../audio/Context';
import { trimBuffer, sliceBuffer, getAudioBuffer } from '../audio/AudioUtils';
import {
  canHover,
  getXFromPointerEvent,
  isElectronApp,
  isIOS,
  isMobileLayout,
  loadFile
} from '../utils';
import { createWaveBlob } from '../utils/index';
import { AddSoundtrackCommand } from 'state/commands/AddSoundtrackCommand';

const MAX_DURATION =
  isElectronApp() || isIOS() ? 0 : MAX_MUSIC_DURATION_WEB_SECONDS;

const formatSeconds = (seconds: number) => {
  seconds = Math.floor(seconds);
  const minutes = Math.floor(seconds / 60);
  seconds %= 60;
  return minutes + ':' + (seconds < 10 ? '0' : '') + seconds;
};

const formatDuration = (seconds: number): string => {
  const condensed = isMobileLayout();
  const minutes = Math.floor(seconds / 60);
  seconds -= minutes * 60;
  let result = '';
  if (minutes) {
    if (condensed) {
      result += minutes + 'm';
    } else {
      result += minutes + ' min' + (minutes > 1 ? 's' : '');
    }
  }
  if (seconds > 0) {
    if (condensed) {
      result += ` ${Math.round(seconds)}s`;
    } else {
      result +=
        (result.length ? ' ' : '') +
        Math.round(seconds) +
        ' sec' +
        (Math.round(seconds) > 1 ? 's' : '');
    }
  }
  return result;
};

const fadeAudioBuffer = (audioBuffer: AudioBuffer, fadeInSeconds: number) => {
  for (let channel = 0; channel < audioBuffer.numberOfChannels; channel += 1) {
    const channelData = audioBuffer.getChannelData(channel);
    const fadeLength = Math.min(
      Math.min(fadeInSeconds, audioBuffer.duration / 2) *
        audioBuffer.sampleRate,
      channelData.length
    );
    for (let i = 0; i < fadeLength && i < channelData.length; i += 1) {
      const fadeOutPos = channelData.length - i - 1;
      channelData[i] = (channelData[i] * i) / fadeLength;
      channelData[fadeOutPos] = (channelData[fadeOutPos] * i) / fadeLength;
    }
  }
};

interface Props {
  audioFile: File | string;
  thumbUrl: string | null;
  title: string | null;
  artist: string | null;
}

interface State {
  buffer: AudioBuffer | null;
  fadeLength: number;
  isProcessing: boolean;
  isPlaying: boolean;
  isSaving: boolean;
  error?: string;
  begin: number;
  end: number;
  screenWidth: number;
}

export class AudioEditing extends React.Component<Props, State> {
  mounted = true;
  audioContext = getAudioContext();
  animationFrameReq = -1;
  lastCurrentTime = 0;
  soundSource: AudioBufferSourceNode | null;
  gainNode: GainNode;
  lastStart: number;
  lastTime: number;
  isDraggingEnd: boolean;
  currentTimeLabel: HTMLElement;
  needle: HTMLElement;
  audioFile: File;
  audioUrl: string | null = null;

  state: State = {
    buffer: null,
    fadeLength: 0,
    isProcessing: true,
    isPlaying: false,
    isSaving: false,
    begin: 0,
    end: 0,
    screenWidth: 0
  };

  async componentDidMount() {
    if (typeof this.props.audioFile === 'string') {
      this.audioUrl = this.props.audioFile as string;
      const blob = await loadFile(this.audioUrl);
      this.audioFile = new File(
        [blob],
        this.audioUrl.split('/').pop() as string
      );
    } else {
      this.audioFile = this.props.audioFile as File;
    }
    const buffer = await getAudioBuffer(this.audioFile);
    this.bufferReady(buffer);
    // window.addEventListener('resize', this.componentDidResize);
  }

  // componentDidResize = () => {
  //   const container = ReactDOM.findDOMNode(this).querySelector('.timeline');
  //   const screenWidth = container.offsetWidth;
  //   this.setState({ screenWidth });
  //   let currentTime = this.getCurrentTime();
  //   const position = currentTime / this.state.buffer.duration;
  //   this.needle.style.transform =
  //     'translateX(' + this.state.screenWidth * position + 'px)';
  // };

  componentWillUnmount() {
    // window.removeEventListener('resize', this.componentDidResize);
    this.mounted = false;
    if (this.soundSource) {
      this.soundSource.disconnect();
    }
    if (this.animationFrameReq > -1) {
      cancelAnimationFrame(this.animationFrameReq);
    }
  }
  bufferReady = (buffer: AudioBuffer) => {
    if (!this.mounted) {
      return;
    }
    if (buffer.duration < 5 * 60) {
      buffer = trimBuffer(buffer);
    }
    if (buffer.duration === 0) {
      this.setState({ error: 'Soundtrack is empty' });
      return;
    }
    const container = (ReactDOM.findDOMNode(this) as HTMLElement).querySelector(
      '.timeline'
    ) as HTMLElement;
    const screenWidth = container.offsetWidth;

    this.gainNode = this.audioContext.createGain();
    this.gainNode.connect(this.audioContext.destination);

    let end = buffer.duration;
    if (MAX_DURATION > 0) {
      end = Math.min(end, MAX_DURATION);
    }
    this.setState(
      {
        buffer,
        isProcessing: false,
        isPlaying: true,
        end,
        screenWidth
      },
      () => {
        this.createSoundSource();
      }
    );
  };
  onWaveformClick = (e: React.MouseEvent<HTMLElement>) => {
    const x =
      e.clientX -
      (
        (ReactDOM.findDOMNode(this) as HTMLElement).querySelector(
          '.timeline'
        ) as HTMLElement
      ).getBoundingClientRect().left;
    const time =
      (x / this.state.screenWidth) *
      (this.state.buffer! as AudioBuffer).duration;
    this.disposeSoundSource();
    this.createSoundSource(time);
    this.setState({ isPlaying: true });
  };
  createSoundSource(time: number = 0) {
    if (this.soundSource) {
      this.disposeSoundSource();
    }
    this.soundSource = this.audioContext.createBufferSource();
    this.soundSource!.buffer = this.state.buffer;

    this.soundSource!.connect(this.gainNode);
    this.soundSource!.start(0, time);
    this.lastStart = Date.now();
    this.lastTime = time;
  }
  disposeSoundSource() {
    if (this.soundSource) {
      this.soundSource.stop(0);
      this.soundSource.disconnect();
      this.soundSource = null;
    }
  }
  startDragBegin = () => {
    this.isDraggingEnd = false;
    window.addEventListener('mousemove', this.didDrag);
    window.addEventListener('touchmove', this.didDrag);

    window.addEventListener('mouseup', this.didStopDrag);
    window.addEventListener('touchend', this.didStopDrag);
    window.addEventListener('touchcancel', this.didStopDrag);
  };
  startDragEnd = () => {
    this.isDraggingEnd = true;
    window.addEventListener('mousemove', this.didDrag);
    window.addEventListener('touchmove', this.didDrag);

    window.addEventListener('mouseup', this.didStopDrag);
    window.addEventListener('touchend', this.didStopDrag);
    window.addEventListener('touchcancel', this.didStopDrag);
  };
  didDrag = (e: MouseEvent | TouchEvent) => {
    const x =
      getXFromPointerEvent(e) -
      (
        (ReactDOM.findDOMNode(this) as HTMLElement).querySelector(
          '.timeline'
        ) as HTMLElement
      ).getBoundingClientRect().left;

    const buffer: AudioBuffer = this.state.buffer!;
    const position = (x / this.state.screenWidth) * buffer.duration;
    let end = this.state.end;
    let begin = this.state.begin;
    if (this.isDraggingEnd) {
      end = Math.max(this.state.begin, Math.min(position, buffer.duration));
      if (MAX_DURATION > 0) {
        begin = Math.max(begin, end - MAX_DURATION);
      }
    } else {
      begin = Math.min(this.state.end, Math.max(position, 0));
      if (MAX_DURATION > 0) {
        end = Math.min(end, begin + MAX_DURATION);
      }
    }
    this.setState({ end, begin });
  };
  didStopDrag = () => {
    window.removeEventListener('mousemove', this.didDrag);
    window.removeEventListener('touchmove', this.didDrag);
    window.removeEventListener('mouseup', this.didStopDrag);
    window.removeEventListener('touchend', this.didStopDrag);
    window.removeEventListener('touchcancel', this.didStopDrag);

    if (this.state.isPlaying) {
      const currentTime = this.getCurrentTime();
      if (currentTime < this.state.begin) {
        this.rewindPlayback();
      } else {
        let newTime = this.state.begin;
        if (this.isDraggingEnd) {
          // play from close the end
          newTime = Math.max(0, this.state.end - 5);
        }
        this.createSoundSource(newTime);
      }
    }
  };
  rewindPlayback = () => {
    this.disposeSoundSource();
    this.createSoundSource(this.state.begin);
    if (this.state.fadeLength > 0) {
      this.gainNode.gain.setValueAtTime(0, this.audioContext.currentTime);
    }
    this.setState({
      isPlaying: true
    });
  };
  pausePlayback = () => {
    this.disposeSoundSource();
    this.lastTime += (Date.now() - this.lastStart) / 1000;
    this.lastCurrentTime = this.getCurrentTime();
    this.setState({
      isPlaying: false
    });
  };
  resumePlayback = () => {
    this.createSoundSource(this.lastTime);
    this.setState({
      isPlaying: true
    });
  };
  doCancel = () => {
    storeDispatch(Actions.closeModal());
  };
  doSave = () => {
    console.log('save');
    this.disposeSoundSource();
    this.setState({ isSaving: true, isPlaying: false }, () => {
      let newBuffer: AudioBuffer = this.state.buffer!;
      let audioFile = this.audioFile;
      if (this.state.begin !== 0 || this.state.end !== newBuffer.duration) {
        console.log('trim audio');
        const start = Math.round(
          (this.state.begin / newBuffer.duration) * newBuffer.length
        );
        const end = Math.round(
          (this.state.end / newBuffer.duration) * newBuffer.length
        );
        newBuffer = sliceBuffer(this.state.buffer!, start, end);
        if (this.state.fadeLength) {
          fadeAudioBuffer(newBuffer, this.state.fadeLength);
        }
        // create wav file from buffer
        audioFile = new File([createWaveBlob(newBuffer)], 'soundtrack.wav');
      } else if (this.state.fadeLength) {
        fadeAudioBuffer(newBuffer, this.state.fadeLength);
        audioFile = new File([createWaveBlob(newBuffer)], 'soundtrack.wav');
      }
      const soundtrack = new Soundtrack(false);
      soundtrack.thumbUrl = this.props.thumbUrl;
      soundtrack.title = this.props.title;
      soundtrack.artist = this.props.artist;
      soundtrack.load(audioFile).then(() => {
        storeDispatch(new AddSoundtrackCommand(soundtrack));
      });
    });
  };
  getCurrentTime() {
    if (this.state.isPlaying) {
      const elapsed = (Date.now() - this.lastStart) / 1000;
      return this.lastTime + elapsed;
    } else {
      return this.lastCurrentTime;
    }
  }
  update = () => {
    let currentTime = this.getCurrentTime();
    const { begin, end } = this.state;
    if (currentTime >= end) {
      // loop
      this.rewindPlayback();
      currentTime = begin;
    }
    const buffer: AudioBuffer = this.state.buffer!;
    if (this.currentTimeLabel) {
      this.currentTimeLabel.style.transform =
        'translateX(' +
        (currentTime / buffer.duration) * this.state.screenWidth +
        'px)';
      this.currentTimeLabel.innerHTML = formatSeconds(currentTime);
    }
    if (this.state.fadeLength > 0) {
      const duration = end - begin;
      const fadeLength = Math.min(this.state.fadeLength, duration / 2);
      let gain = 1;
      if (currentTime < begin + fadeLength) {
        if (currentTime === begin) {
          gain = 0;
        } else {
          gain = (currentTime - begin) / fadeLength;
        }
      } else if (currentTime > end - fadeLength) {
        if (currentTime === end) {
          gain = 0;
        } else {
          gain = (end - currentTime) / fadeLength;
        }
      }
      this.gainNode.gain.setValueAtTime(gain, this.audioContext.currentTime);
    }
    const position = currentTime / buffer.duration;
    this.needle.style.transform =
      'translateX(' + this.state.screenWidth * position + 'px)';
    this.animationFrameReq = requestAnimationFrame(this.update);
  };
  componentDidUpdate(prevProps: any, prevState: State) {
    if (prevState.isPlaying !== this.state.isPlaying) {
      if (this.state.isPlaying) {
        this.update();
      } else {
        cancelAnimationFrame(this.animationFrameReq);
        this.animationFrameReq = -1;
      }
    }
  }
  didChangeFade = (e: React.ChangeEvent<HTMLSelectElement>) => {
    this.disposeSoundSource();
    this.createSoundSource(this.state.begin);

    const fadeLength = parseFloat(e.target.value);
    if (fadeLength === 0) {
      this.gainNode.gain.setValueAtTime(1, this.audioContext.currentTime);
    } else {
      this.gainNode.gain.setValueAtTime(0, this.audioContext.currentTime);
    }
    this.setState({
      isPlaying: true,
      fadeLength
    });
  };
  render() {
    const isProcessing = this.state.isProcessing || this.state.isSaving;
    return (
      <div className="audio-editing">
        <div className="content">
          <h1>
            {this.state.error
              ? `Error: ${this.state.error}`
              : `Video duration: ${
                  this.state.isProcessing
                    ? 'N/A'
                    : formatDuration(this.state.end - this.state.begin)
                }`}
          </h1>
          {this.state.buffer &&
            MAX_DURATION > 0 &&
            (this.state.buffer! as AudioBuffer).duration > MAX_DURATION && (
              <div className="message">
                Your video can't be longer than 90 seconds. Please choose the
                portion of the song to use.
                <br />
                Use the{' '}
                <a href="//beatflyer.com/download" target="_blank">
                  desktop app
                </a>{' '}
                if you want to record longer videos.
              </div>
            )}
          <div className="timeline">
            {this.state.buffer && !this.state.isSaving && (
              <div className="waveform">
                <WaveformView
                  buffer={this.state.buffer!}
                  width={this.state.screenWidth}
                  height={200}
                  onClick={this.onWaveformClick}
                />
                <div
                  ref={el => (this.needle = el as HTMLElement)}
                  className="needle"
                />
              </div>
            )}
            {isProcessing && this.state.error === undefined && (
              <div className="processing">
                {this.state.isProcessing ? 'Processing' : 'Saving'}
                ...
              </div>
            )}
            {!isProcessing && (
              <div
                className="begin"
                style={{
                  transform:
                    'translateX(' +
                    this.state.screenWidth *
                      (this.state.begin /
                        (this.state.buffer! as AudioBuffer).duration -
                        1) +
                    'px)'
                }}
              >
                <div
                  className="needle"
                  onMouseDown={canHover() ? this.startDragBegin : undefined}
                  onTouchStart={canHover() ? undefined : this.startDragBegin}
                />
                <div className="time">{formatSeconds(this.state.begin)}</div>
              </div>
            )}
            {!isProcessing && (
              <div
                className="end"
                style={{
                  transform:
                    'translateX(' +
                    (this.state.screenWidth * this.state.end) /
                      (this.state.buffer! as AudioBuffer).duration +
                    'px)'
                }}
              >
                <div
                  className="needle"
                  onMouseDown={canHover() ? this.startDragEnd : undefined}
                  onTouchStart={canHover() ? undefined : this.startDragEnd}
                />
                <div className="time">{formatSeconds(this.state.end)}</div>
              </div>
            )}
            {!isProcessing && (
              <div
                className="currentTime"
                ref={el => (this.currentTimeLabel = el as HTMLElement)}
              >
                00:00
              </div>
            )}
          </div>
          <div className="playback">
            <button
              className="rewind"
              disabled={isProcessing}
              onClick={this.rewindPlayback}
            />
            {this.state.isPlaying ? (
              <button
                className="pause"
                disabled={isProcessing}
                onClick={this.pausePlayback}
              />
            ) : (
              <button
                className="play"
                disabled={isProcessing}
                onClick={this.resumePlayback}
              />
            )}
          </div>
          <div className="fade">
            Fade in and out:{' '}
            <select
              value={this.state.fadeLength}
              disabled={isProcessing}
              onChange={this.didChangeFade}
            >
              <option value={0}>Don't fade</option>
              <option value={0.5}>0.5 second</option>
              <option value={1}>1 second</option>
              <option value={5}>5 seconds</option>
            </select>
          </div>
          <div className="buttons">
            <button
              className="large secondary"
              onClick={this.doCancel}
              disabled={this.state.isSaving}
            >
              Cancel
            </button>
            <button
              className="large primary"
              onClick={this.doSave}
              disabled={isProcessing}
            >
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/audio-editing/AudioEditing.tsx